import React, { useEffect, useRef, useState } from 'react';
import Logo from '../Logo';
import Icon, { IconName } from '../Icon';
import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher';
import { useTranslation } from '../../i18n';
import { useTheme } from '../../contexts/ThemeContext';
import { useThemeChoice } from '../../hooks/useThemeChoice';
import { TabType } from '../../hooks/useNavigation';
import { User } from '../../types/auth';
import './DesktopHeader.css';
import { formatPercent } from '../../utils/format';
import { sitePageUrl } from '../../utils/siteLinks';

interface DesktopHeaderProps {
  activeTab: TabType | null;
  onTabChange: (tab: TabType) => void;
  onAddGoal: () => void;
  onOpenSuggestion: () => void;
  overallProgress: number;
  completedGoalsCount: number;
  totalGoalsCount: number;
  isScrolled: boolean;
  user: User | null;
  onLogout: () => void;
}


const TABS: Array<{ tab: TabType; icon: IconName }> = [
  { tab: 'home', icon: 'home' },
  { tab: 'goals', icon: 'goal' },
  { tab: 'ongoing', icon: 'playCircle' },
  { tab: 'timely', icon: 'clock' },
  { tab: 'achievements', icon: 'trophy' },
];

const DesktopHeader: React.FC<DesktopHeaderProps> = ({
  activeTab,
  onTabChange,
  onAddGoal,
  onOpenSuggestion,
  overallProgress,
  completedGoalsCount,
  totalGoalsCount,
  isScrolled,
  user,
  onLogout
}) => {
  const { t, language } = useTranslation();
  const { resolvedTheme } = useTheme();
  // The switch saves to the account too, like Profile (T-128)
  const chooseTheme = useThemeChoice();
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
  const accountMenuRef = useRef<HTMLDivElement>(null);
  const accountButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!isAccountMenuOpen) return;

    const handlePointerDown = (event: MouseEvent) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(event.target as Node)) {
        setIsAccountMenuOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsAccountMenuOpen(false);
        accountButtonRef.current?.focus();
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isAccountMenuOpen]);

  const runAndClose = (action: () => void) => () => {
    action();
    setIsAccountMenuOpen(false);
  };

  const isDark = resolvedTheme === 'dark';
  const initial = (user?.firstName?.[0] || user?.username?.[0] || 'U').toUpperCase();

  return (
    <div className={`desktop-header-container ${isScrolled ? 'scrolled' : ''}`}>
      <header className="desktop-header">
        <div className="desktop-header-content">
          <div className="desktop-header-left">
            <div className="desktop-brand">
              <Logo size={40} className="desktop-logo" />
              <span className="desktop-title">Riselio</span>
            </div>
          </div>

          <div className="desktop-header-center">
            <div className="desktop-stats">
              <span className="desktop-stat">
                {completedGoalsCount}/{totalGoalsCount} {t('stats.completedGoals')} ({formatPercent(overallProgress, language)})
              </span>
            </div>
          </div>

          <div className="desktop-header-right" ref={accountMenuRef}>
            <button
              ref={accountButtonRef}
              type="button"
              className="desktop-profile-btn"
              onClick={() => setIsAccountMenuOpen((open) => !open)}
              aria-haspopup="menu"
              aria-expanded={isAccountMenuOpen}
              aria-controls="desktop-account-menu"
            >
              <span className="desktop-profile-avatar">{initial}</span>
              <span className="desktop-user-info">
                <span className="desktop-user-greeting">{t('auth.welcomeGreeting')}</span>
                <span className="desktop-user-name">
                  {user?.firstName || user?.username || t('profile.firstName')}
                </span>
              </span>
              <Icon name={isAccountMenuOpen ? 'chevronUp' : 'chevronDown'} className="desktop-profile-chevron" />
            </button>

            {isAccountMenuOpen && (
              <div id="desktop-account-menu" className="desktop-account-menu" role="menu" aria-label={t('navigation.accountMenu')}>
                <button
                  type="button"
                  role="menuitem"
                  className="desktop-account-menu__item"
                  onClick={runAndClose(() => { window.location.hash = '#/profile'; })}
                >
                  <Icon name="user" className="desktop-account-menu__icon" />
                  {t('navigation.profile')}
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="desktop-account-menu__item"
                  onClick={runAndClose(() => { window.location.hash = '#/settings'; })}
                >
                  <Icon name="settings" className="desktop-account-menu__icon" />
                  {t('navigation.settings')}
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="desktop-account-menu__item"
                  onClick={runAndClose(onOpenSuggestion)}
                >
                  <Icon name="lightbulb" className="desktop-account-menu__icon" />
                  {t('navigation.suggestion')}
                </button>
                <a
                  role="menuitem"
                  className="desktop-account-menu__item"
                  href={sitePageUrl('contact', language)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setIsAccountMenuOpen(false)}
                >
                  <Icon name="mail" className="desktop-account-menu__icon" />
                  {t('navigation.contact')}
                  <Icon name="open" className="desktop-account-menu__trailing" />
                </a>

                <div className="desktop-account-menu__divider" role="separator" />

                <button
                  type="button"
                  role="menuitemcheckbox"
                  aria-checked={isDark}
                  className="desktop-account-menu__item"
                  onClick={() => chooseTheme(isDark ? 'light' : 'dark')}
                >
                  <Icon name={isDark ? 'moon' : 'sun'} className="desktop-account-menu__icon" />
                  {t('navigation.theme')}
                  <span className="desktop-account-menu__trailing">
                    {isDark ? t('profile.theme.dark') : t('profile.theme.light')}
                  </span>
                </button>
                <div className="desktop-account-menu__row">
                  <span className="desktop-account-menu__row-label">
                    <Icon name="globe" className="desktop-account-menu__icon" />
                    {t('navigation.language')}
                  </span>
                  <LanguageSwitcher variant="surface" />
                </div>

                <div className="desktop-account-menu__divider" role="separator" />

                <button
                  type="button"
                  role="menuitem"
                  className="desktop-account-menu__item is-danger"
                  onClick={runAndClose(onLogout)}
                >
                  <Icon name="logout" className="desktop-account-menu__icon" />
                  {t('navigation.logout')}
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <nav className="desktop-navigation" aria-label={t('navigation.mainNav')}>
        <div className="desktop-nav-content">
          <div className="desktop-nav-tabs">
            {TABS.map(({ tab, icon }) => {
              const isActive = activeTab === tab;
              return (
                <button
                  key={tab}
                  type="button"
                  className={`desktop-nav-tab ${isActive ? 'active' : ''}`}
                  onClick={() => onTabChange(tab)}
                  aria-current={isActive ? 'page' : undefined}
                >
                  <Icon name={icon} className="desktop-nav-icon" />
                  <span>{t(`navigation.${tab}`)}</span>
                </button>
              );
            })}
          </div>

          <button
            type="button"
            className="desktop-add-goal-btn"
            onClick={onAddGoal}
            aria-label={t('goals.addNew')}
            title={t('goals.addNew')}
          >
            <Icon name="plus" className="desktop-add-goal-icon" />
            <span className="desktop-add-goal-text">{t('goals.addNew')}</span>
          </button>
        </div>
      </nav>
    </div>
  );
};

export default DesktopHeader;
